import React from 'react';

import {
  resolveBoundStatus,
  type BoundEmptyReason,
  type BoundStatus,
  type BoundStatusInput,
} from '../../utils/boundStatus/resolveBoundStatus';

/**
 * A state's content, or a function that builds it from the resolved status.
 *
 * The function form takes the whole status rather than just the surface,
 * because the surface is not the only thing the resolver decided.
 * `resolveBoundStatus` reports `ageMs` and `isStale` on every surface
 * deliberately -- a component that is refetching or showing an error still has
 * whatever it drew last on screen, and "this failed, and what you are looking
 * at is an hour old" is the sentence a reader needs. Handing the slot only its
 * surface would force any consumer wanting that sentence to resolve the same
 * status a second time beside the shell, which is how the two copies drift.
 */
export type BoundStatusSlot =
  | React.ReactNode
  | ((status: BoundStatus) => React.ReactNode);

/**
 * The status an empty slot receives, whose reason is always known.
 *
 * `emptyReason` is optional on `BoundStatus` because it is meaningless on the
 * other three surfaces, but it is always set alongside an empty one. Narrowing
 * it here keeps the distinction between "the query returned nothing" and
 * "nothing survived my filters" a total choice for the consumer instead of one
 * it has to defend against `undefined` to make.
 */
export type BoundEmptyStatus = BoundStatus & {
  readonly emptyReason: BoundEmptyReason;
};

export interface BoundStatusSlots {
  readonly loading?: BoundStatusSlot;
  readonly error?: BoundStatusSlot;
  readonly empty?:
    | React.ReactNode
    | ((status: BoundEmptyStatus) => React.ReactNode);
}

export interface BoundStatusShellProps extends BoundStatusInput {
  readonly slots?: BoundStatusSlots;
  readonly children: React.ReactNode;
}

/** `ReactNode` holds nothing callable, so this discriminates the union. */
function isRenderFn<T>(
  slot: React.ReactNode | ((status: T) => React.ReactNode),
): slot is (status: T) => React.ReactNode {
  return typeof slot === 'function';
}

/**
 * Renders one of a bound component's non-data states, or its children.
 *
 * Slots rather than a rendered frame: this component stays off `./ce-runtime`
 * because React slot props are not serializable across that boundary. Only
 * `resolveBoundStatus` ships through the runtime entry; the shell lives on the
 * main package export for hosts that can render React. Each consumer supplies
 * its own states -- the dashboard forwards a surface prop to a hen-ui frame, the
 * repeater renders its own skeleton -- and what they share is the decision, not
 * the markup.
 *
 * A missing slot renders nothing. That is the honest default for a shell that
 * does not know its consumer's design language, and it is safe because the
 * alternative -- falling through to `children` -- would render a chart against
 * data that failed to load.
 */
export function BoundStatusShell({
  slots,
  children,
  ...input
}: BoundStatusShellProps) {
  const status = resolveBoundStatus(input);

  switch (status.surface) {
    case 'loading': {
      const slot = slots?.loading;
      return <>{(isRenderFn(slot) ? slot(status) : slot) ?? null}</>;
    }
    case 'error': {
      const slot = slots?.error;
      return <>{(isRenderFn(slot) ? slot(status) : slot) ?? null}</>;
    }
    case 'empty': {
      const slot = slots?.empty;
      if (!isRenderFn<BoundEmptyStatus>(slot)) {
        return <>{slot ?? null}</>;
      }
      return (
        <>
          {slot({
            ...status,
            emptyReason: status.emptyReason ?? 'source-empty',
          }) ?? null}
        </>
      );
    }
    case 'ready':
      return <>{children}</>;
  }
}
